import { getPlayPositionString } from "@Components/game/scenario-editor/helpers";
import Icon from "@Components/generic/icon";
import { UnityBridge } from "@Context/common/general/context";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PhaseOfPlayMap from "@Enums/PhaseOfPlayMap";
import { faPlayCircle } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Box, Checkbox, Chip, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";

interface ITabContent {
  scenarios: ScenarioResponse[];
  selectedScenarios: number[];
  onScenarioSelected: (scenario: ScenarioResponse) => void;
}

const TabContent = ({ scenarios, selectedScenarios, onScenarioSelected }: ITabContent) => {
  const theme = useTheme();
  const { i18n } = useI18n();

  const handlePlayScenario = (e: React.MouseEvent, scenario: ScenarioResponse) => {
    e.stopPropagation();
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.PlayTest,
      payload: {
        id: scenario.doc_id,
        isStaging: false,
      },
    });
  };

  if (scenarios.length === 0) {
    return (
      <Stack spacing={1} sx={{ justifyContent: "center", alignItems: "center", minHeight: theme.spacing(20) }}>
        <Typography variant="textLg500" color="dsNeutral.v400">
          {i18n("playlists.playlist.selection.no.scenarios")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ flex: 1, overflow: "auto" }}>
      {scenarios.map((scenario) => (
        <Box
          key={scenario.id}
          sx={{
            padding: theme.spacing(2),
            border: "1px solid",
            borderColor: selectedScenarios.includes(scenario.id) ? theme.palette.primary.main : theme.palette.dsSurface.v300,
            borderRadius: theme.spacing(1),
            backgroundColor: "dsBase.v100",
            cursor: "pointer",
            "&:hover": {
              borderColor: theme.palette.dsSurface.v400,
            },
            position: "relative",
          }}
          onClick={() => onScenarioSelected(scenario)}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Checkbox
              checked={selectedScenarios.includes(scenario.id)}
              onClick={(e) => {
                e.stopPropagation();
                onScenarioSelected(scenario);
              }}
              sx={{
                color: theme.palette.dsBase.v200,
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              <Typography variant="textLg700" color="dsBase.v200">
                {scenario.name}
              </Typography>
              <Typography variant="textBase500" color="dsNeutral.v400">
                {`${PhaseOfPlayMap[scenario.phase_of_play as PhaseOfPlay]} · ${getPlayPositionString(scenario.play_positions)}`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color={scenario.published ? "success" : "warning"} label={scenario.published ? "published" : "draft"} />
              <IconButton
                onClick={(e) => handlePlayScenario(e, scenario)}
                sx={{
                  color: theme.palette.dsBase.v200,
                }}
              >
                <Icon icon={faPlayCircle} sx={{ fontSize: theme.spacing(4) }} />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default TabContent;
